import React, { useState, useEffect } from 'react';
import DataTable from '../DataTable';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { useJunoCommonFunctions } from '../../../client/Juno/helpers';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import StatementsTableActions from './StatementsTableActions';
import AddNewStatementModal from './StatementModal.jsx';
import Pagination from '../Pagination';
import JunoButton from '../common/JunoButton';
import StatementMobileComponent from './StatementMobileComponent.jsx';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Add from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import EmptyCardState from '../common/EmptyCardState.jsx';
import clsx from 'clsx';
import { cryptoCurrencyList, fiatCurrencyList } from '../../helpers/constant.js';

const StatementTable = ({ isDashboardPage, activeCurrencyPage }) => {
    const [addNewStatementModal, setAddNewStatementModal] = useState(false);
    const { getStatements } = useJunoCommonFunctions();
    const navigate = useNavigate();

    const image_base_url = useSelector(state => state.config.image_base_url);
    const statementsData = useSelector(state => state.juno.statements);
    const clientData = useSelector(state => state.juno.clientData);
    const [paginatedData, setPaginatedData] = useState([]);
    const [limit, setLimit] = useState(10);
    const location = useLocation();
    const navigateState = location.state;

    const { readOnly } = clientData || {};

    useEffect(() => {
        if (clientData) {
            getStatements();
        }
    }, [clientData, activeCurrencyPage]);

    function handleCreateNew() {
        if (readOnly) return;
        setAddNewStatementModal(true);
    }

    function onCloseStatementModal() {
        setAddNewStatementModal(false);
    }

    function handleSeeAll() {
        navigate('/client/statements');
    }

    const transformedStatementsData = isDashboardPage ? statementsData?.slice(0, 5) : paginatedData;

    const overviewTableConfig = {
        columns: [
            { key: 'createdAt', label: 'Date' },
            { key: 'currency', label: 'CURRENCY', noSort: true },
            { key: 'openingBalance', label: 'OPENING BALANCE' },
            { key: 'closingBalance', label: 'CLOSING BALANCE' },
            // { key: 'actions', label: 'ACTIONS', noSort: true },
        ],

        customRenderCell: (col, row) => {
            if (col.key === 'createdAt') {
                return (
                    <span>
                        {row.createdAt ? (
                            <>
                                <span>{format(new Date(row.createdAt), 'dd.MM.y')}</span>
                                <span className="ml-2">
                                    {format(new Date(row.createdAt), 'HH:mm')}
                                </span>
                            </>
                        ) : (
                            'N/A'
                        )}
                    </span>
                );
            }

            if (col.key === 'currency') {
                let matchedCurrency;
                if (activeCurrencyPage === 'crypto') {
                    matchedCurrency = cryptoCurrencyList?.find(x => x.currencyShortName === row.currencyShortName);
                } else {
                    matchedCurrency = fiatCurrencyList?.find(x => x.currencyShortName === row.currencyShortName);
                }

                return (
                    <span className="inline-flex items-center gap-2">
                        {matchedCurrency?.currencyIcon && (
                            <img
                                src={`${image_base_url}${matchedCurrency?.currencyIcon}`}
                                alt={matchedCurrency.currencyShortName}
                                className="w-4 h-4"
                            />
                        )}
                        <span>{matchedCurrency?.currencyShortName || row?.currencyShortName}</span>
                    </span>
                );
            }

            if (col.key === 'closingBalance') {
                return (
                    <div className="flex justify-between">
                        <span className="block">{row.closingBalance}</span>
                        <StatementsTableActions row={row} />
                    </div>
                );
            }

            return null;
        },
    };
    const statementPage = location.pathname.includes('/client/statements');

    const EmptyScreenCard = ({ className, transformedStatementsData, statementPage }) => {
        return !transformedStatementsData?.length ? (
            <div
                className={clsx(
                    statementPage
                        ? 'absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-6'
                        : 'w-full py-4 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-1',
                    className,
                )}>
                {statementPage && (
                    <p className="text-text text-sm font-semibold text-center uppercase">
                        There are no statements yet.
                    </p>
                )}
                <EmptyCardState
                    data-e2e="create-statement"
                    cardHeading={'create new statement'}
                    className={`${statementPage ? 'w-[320px]' : 'w-full h-[132px]'}`}
                    onClick={handleCreateNew}
                />
            </div>
        ) : null;
    };

    return (
        <div className="m-1 lg:!mt-[3px]">
            <div
                className={`relative flex flex-col items-start lg:justify-between overflow-x-auto ${transformedStatementsData?.length === 0 ? 'rounded-lg lg:rounded-lg' : 'rounded-t-lg lg:rounded-t-lg lg:rounded-b-none'} md:mx-0 border !border-section-br
        ${statementPage ? 'xxs:justify-center !text-text flex justify-between items-start  overflow-x-auto rounded-lg bg-[#FFFFFF] dark:bg-[#26272B] py-3 mb-1 xxs:m-0' : 'justify-between font-lg'} 
        bg-[#FFFFFF] dark:bg-[#26272B] px-4 py-3 md:pb-0  mb-0 lg:m-0 ${isDashboardPage ? '!border-b-0 xxs:!m-0' : ''} ${statementPage && !transformedStatementsData?.length ? 'lg:min-h-[85vh]' : ''}`}>
                <div
                    className={`flex relative w-full items-center ${statementPage ? 'lg:justify-between justify-center' : 'justify-between'}`}>
                    {statementPage && (
                        <div className="absolute left-0 lg:hidden">
                            <ArrowBackIcon
                                className="cursor-pointer text-black dark:!text-white"
                                onClick={() => {
                                    navigate(navigateState?.onBack || '/client');
                                }}
                            />
                        </div>
                    )}
                    <p
                        className={`lg:text-sm lg:font-semibold lg:leading-5 !text-section-title font-semibold text-[16px] leading-[20px] tracking-normal text-center lg:text-left 
    ${statementPage ? 'md:dark:!text-white' : ''}`}>
                        STATEMENTS
                    </p>

                    {transformedStatementsData?.length ? (
                        <div className="absolute mr-[-13px] right-4 flex gap-[14px] lg:hidden z-10">
                            <Add
                                className="cursor-pointer text-black dark:!text-white"
                                onClick={handleCreateNew}
                            />
                        </div>
                    ) : null}

                    {transformedStatementsData?.length > 0 && (
                        <div>
                            <div className="flex gap-2 !right-auto">
                                <JunoButton
                                    className={clsx(
                                        'hidden lg:flex !w-[97px] text-xs !p-0 !h-7 font-semibold',
                                        readOnly && '!cursor-not-allowed',
                                    )}
                                    disabled={readOnly}
                                    type="primary"
                                    onClick={handleCreateNew}>
                                    CREATE NEW
                                </JunoButton>
                                {isDashboardPage && (
                                    <JunoButton
                                        className="hidden lg:block !w-[66px] text-xs !p-0 !h-7 font-semibold"
                                        type="secondary"
                                        onClick={handleSeeAll}>
                                        See all
                                    </JunoButton>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {!statementPage ? (
                    <EmptyScreenCard
                        className={"!flex lg:!grid"}
                        transformedStatementsData={transformedStatementsData}
                        statementPage={statementPage}
                    />
                ) : null}
            </div>

            {statementPage ? (
                <EmptyScreenCard
                    transformedStatementsData={transformedStatementsData}
                    statementPage={statementPage}
                />
            ) : null}

            {transformedStatementsData.length > 0 && (
                <div className="hidden lg:block">
                    <DataTable
                        data={transformedStatementsData || []}
                        columns={overviewTableConfig.columns}
                        hasNestedRows={false}
                        customRenderCell={overviewTableConfig.customRenderCell}
                        className={!isDashboardPage && 'rounded-b-none'}
                    />
                </div>
            )}
            <div
                className={`lg:hidden block overflow-x-auto rounded-[8px] lg:m-2 !mt-0 pt-0 lg:!border-t-0 lg:!rounded-t-none dark:!bg-[#26272B] dark:!border-0  ${
                    statementPage
                        ? '  dark:!bg-[#26272B] dark:!text-white  '
                        : ' border border-gray-300 dark:border-gray-700'
                } bg-white dark:bg-[#26272B] ${isDashboardPage ? '!border-t-0 !rounded-t-none ' : ''}`}>
                <StatementMobileComponent
                    tableData={transformedStatementsData || []}
                    handleSeeAll={handleSeeAll}
                    activeCurrencyPage={activeCurrencyPage}
                    isDashboardPage={isDashboardPage}
                />
            </div>

            <AddNewStatementModal
                open={addNewStatementModal}
                onClose={onCloseStatementModal}
                updateStatements={getStatements}
                fullScreen={true}
            />
            {!isDashboardPage && (statementsData?.length || transformedStatementsData?.length) ? (
                <div className="flex justify-center py-4 bg-[#FFFFFF] rounded-b-lg dark:bg-[#26272B]">
                    <Pagination
                        data={statementsData}
                        pageSize={limit}
                        setPaginatedData={setPaginatedData}
                    />
                </div>
            ) : null}
        </div>
    );
};

StatementTable.propTypes = {
    isDashboardPage: PropTypes.bool.isRequired,
    activeCurrencyPage: PropTypes.string.isRequired,
};

export default StatementTable;